import Script from "next/script";
import type {
  ComponentProps,
  Dispatch,
  ReactNode,
  SetStateAction,
} from "react";
import { use, useEffect } from "react";
import { useState } from "react";
import { createContext } from "react";

type ScriptProps = Omit<ComponentProps<typeof Script>, "src" | "onReady"> & {
  src: string;
};

type ScriptLoaderContextState = {
  [key: string]: {
    ready: boolean;
    props: ScriptProps;
  };
};

export const ScriptLoaderContext = createContext<
  [ScriptLoaderContextState, Dispatch<SetStateAction<ScriptLoaderContextState>>]
>(null!);

export const ScriptLoaderProvider = ({ children }: { children: ReactNode }) => {
  const state = useState<ScriptLoaderContextState>({});

  return (
    <ScriptLoaderContext value={state}>
      {Object.values(state[0]).map(({ props }) => (
        <Script
          key={props.src}
          onReady={() => {
            state[1]((prev) => ({
              ...prev,
              [props.src]: {
                ...prev[props.src],
                ready: true,
              },
            }));
          }}
          {...props}
        />
      ))}
      {children}
    </ScriptLoaderContext>
  );
};

export const useScriptLoader = (props: ScriptProps) => {
  const [state, setState] = use(ScriptLoaderContext);

  useEffect(() => {
    setState((prev) => {
      if (props.src in prev) return prev;
      return {
        ...prev,
        [props.src]: {
          ready: false,
          props,
        },
      };
    });
  }, [props.src]);

  return !!state[props.src]?.ready;
};
